import { useState } from 'react'
import { Lock, Send, CheckCircle, AlertCircle } from '@/ui/icons'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/ui/components'
import { Button } from '@/ui/design-system'
import { TripsApi } from '@/shared/api/trips'
import { ApiError } from '@/shared/api/types'
import { useGuestSession } from '@/modules/auth'
import { useAuth } from '@/modules/auth'
import type { TripWithDetailsResponse } from '@/shared/api'

interface GuestAccessDeniedProps {
  trip: TripWithDetailsResponse
  tripId: string
  onAccessRequested?: () => void
}

export function GuestAccessDenied({ trip, tripId, onAccessRequested }: GuestAccessDeniedProps) {
  const { guestSession } = useGuestSession()
  const { getAccessToken } = useAuth()
  const [isRequesting, setIsRequesting] = useState(false)
  const [requestStatus, setRequestStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleRequestAccess = async () => {
    if (!guestSession || !guestSession.guestAccessToken) {
      setErrorMessage('Guest session not found. Please join the trip again.')
      setRequestStatus('error')
      return
    }

    setIsRequesting(true)
    setRequestStatus('idle')
    setErrorMessage(null)

    try {
      const apiClient = new TripsApi(getAccessToken)
      // Backend will look up guest member ID from access token
      await apiClient.requestGuestAccess(tripId, {
        accessToken: guestSession.guestAccessToken
      })

      setRequestStatus('success')
      if (onAccessRequested) {
        onAccessRequested()
      }
    } catch (error) {
      
      // Check if it's a 409 (already pending) - show success message instead
      if (error instanceof ApiError && error.status === 409) {
        setRequestStatus('success')
        setErrorMessage(null)
      } else if (error instanceof Error && error.message.includes('already pending')) {
        setRequestStatus('success')
        setErrorMessage(null)
      } else {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to request access. Please try again.'
        )
        setRequestStatus('error')
      }
    } finally {
      setIsRequesting(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
          </div>
          <CardTitle>Access Restricted</CardTitle>
          <CardDescription className="mt-2">
            You've joined this trip, but the trip owner hasn't granted you access to view trip details.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg bg-muted/50 p-4 space-y-2">
            <div className="text-sm font-medium">Trip: {trip.name}</div>
            {trip.description && (
              <div className="text-sm text-muted-foreground">{trip.description}</div>
            )}
          </div>

          {requestStatus === 'success' ? (
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-green-900 dark:text-green-100">
                    Request Sent
                  </div>
                  <div className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Your access request has been sent to the trip owner. You'll be notified when they respond.
                  </div>
                </div>
              </div>
            </div>
          ) : requestStatus === 'error' ? (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-red-900 dark:text-red-100">
                    Request Failed
                  </div>
                  <div className="text-sm text-red-700 dark:text-red-300 mt-1">
                    {errorMessage || 'Failed to send access request. Please try again.'}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Button
              onClick={handleRequestAccess}
              disabled={isRequesting}
              className="w-full"
              size="lg"
            >
              {isRequesting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Sending Request...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Request Access
                </>
              )}
            </Button>
          )}

          <div className="text-xs text-center text-muted-foreground pt-2">
            The trip owner will be notified of your request and can grant you access to view trip details.
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

